import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, scaricaFile } from "@/lib/api";
import { euro, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2, AlertTriangle } from "lucide-react";

const oggi = () => new Date().toISOString().slice(0, 10);

export function ImportGiacenzeDialog({ open, onClose }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [data, setData] = useState(oggi());
  const [anteprima, setAnteprima] = useState(null);
  const [busy, setBusy] = useState(false);
  const onError = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
  const fd = (f) => { const d = new FormData(); d.append("file", f); return d; };

  const carica = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true); setAnteprima(null);
    try { setAnteprima((await api.post("/magazzino/giacenze/import/anteprima", fd(f))).data); }
    catch (e) { onError(e); setFile(null); } finally { setBusy(false); }
  };
  const conferma = async () => {
    setBusy(true);
    try {
      const r = (await api.post(`/magazzino/giacenze/import/conferma?data=${data}`, fd(file))).data;
      toast.success(`${r.importati} giacenze caricate (valore ${euro(r.valore)})`);
      ["mag-giacenze", "mag-carichi", "mag-articoli"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onClose();
    } catch (e) { onError(e); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="import-giacenze-dialog">
        <DialogHeader><DialogTitle>Importa giacenze iniziali da Excel</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          Scarica il template (contiene già tutti gli articoli e le dimensioni), compila <b>Quantità</b> e, se vuoi, <b>Costo unitario</b>. Le righe vuote o a 0 vengono ignorate. L'import crea un unico carico <b>"GIACENZA INIZIALE"</b> senza fornitore né commessa.
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Button variant="outline" onClick={() => scaricaFile("/magazzino/giacenze/import/modello", (m) => toast.error(m))} data-testid="import-giacenze-template"><Download className="h-4 w-4 mr-1.5" /> Scarica template</Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => carica(e.target.files?.[0])} data-testid="import-giacenze-file" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-giacenze-scegli"><Upload className="h-4 w-4 mr-1.5" /> {file ? "Cambia file" : "Scegli file compilato"}</Button>
          <span className="text-sm text-slate-500" data-testid="import-giacenze-nomefile">{file?.name || ""}</span>
          <div className="ml-auto space-y-1"><Label className="text-xs text-slate-500">Data inventario</Label><Input type="date" className="h-9 w-[160px]" value={data} onChange={(e) => setData(e.target.value)} data-testid="import-giacenze-data" /></div>
        </div>
        {busy && !anteprima && <div className="text-sm text-slate-500">Analisi del file in corso…</div>}
        {anteprima && (
          <div className="flex-1 min-h-0 flex flex-col gap-3">
            <div className="flex gap-3 text-sm flex-wrap" data-testid="import-giacenze-riepilogo">
              <div className="rounded-lg border px-3 py-2"><div className="text-xs text-slate-500">Righe trovate</div><b data-testid="giac-trovati">{anteprima.totale}</b></div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"><div className="text-xs text-emerald-700">Da caricare</div><b className="text-emerald-700" data-testid="giac-validi">{anteprima.validi}</b></div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"><div className="text-xs text-amber-700">Con errori (saltate)</div><b className="text-amber-700" data-testid="giac-errori">{anteprima.errori}</b></div>
              <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2"><div className="text-xs text-slate-600">Valore inventario</div><b className="font-mono text-primary" data-testid="giac-valore">{euro(anteprima.valore)}</b></div>
            </div>
            <div className="overflow-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0"><tr>
                  <th className="px-2 py-1.5 text-left">Riga</th><th className="px-2 py-1.5 text-left">Codice</th><th className="px-2 py-1.5 text-left">Articolo</th><th className="px-2 py-1.5 text-left">Dimensione</th><th className="px-2 py-1.5 text-right">Quantità</th><th className="px-2 py-1.5 text-center">U.M.</th><th className="px-2 py-1.5 text-right">€/U</th><th className="px-2 py-1.5 text-right">Importo</th><th className="px-2 py-1.5 text-left min-w-[240px]">Esito</th>
                </tr></thead>
                <tbody>
                  {anteprima.righe.map((r) => (
                    <tr key={r.riga} className={`border-t ${r.errori.length ? "bg-amber-50/60" : ""}`} data-testid={`giac-riga-${r.riga}`}>
                      <td className="px-2 py-1.5 font-mono text-slate-400">{r.riga}</td><td className="px-2 py-1.5 font-mono">{r.codice}</td><td className="px-2 py-1.5">{r.descrizione}</td><td className="px-2 py-1.5">{r.variante}</td><td className="px-2 py-1.5 text-right font-mono">{num(r.quantita, 2)}</td><td className="px-2 py-1.5 text-center">{r.um}</td><td className="px-2 py-1.5 text-right font-mono">{euro(r.costo_unitario)}</td><td className="px-2 py-1.5 text-right font-mono">{euro(r.importo)}</td>
                      <td className="px-2 py-1.5">{r.errori.length ? <span className="flex items-start gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{r.errori.join("; ")}</span> : <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> OK</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={conferma} disabled={!anteprima || anteprima.validi === 0 || busy || !data} data-testid="import-giacenze-conferma">Carica {anteprima?.validi || 0} giacenze</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
